/**
 * Core async fetching state machine — handles paginated requests with
 * cursor tracking, stale-request flushing, loading states, and scroll-based
 * pagination.
 *
 * Manages the request lifecycle (initial → paginate → search) and the
 * open/close reset logic. Composed by useAsyncOptions.
 */
import type { ActionListOption } from '@latitude/action-list';
import { type UIEvent, useCallback, useRef, useState } from 'react';
import { shouldLoadMore } from 'utils';

import type {
  MakeRequestArgs,
  UsePaginatedFetchParams,
  UsePaginatedFetchReturn,
} from './types';

export const usePaginatedFetch = <T = undefined>({
  loadOptions,
  onOpenChange: passedOnOpenChange,
}: UsePaginatedFetchParams<T>): UsePaginatedFetchReturn<T> => {
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [options, setOptions] = useState<ActionListOption<T>[]>([]);
  const [loading, setLoading] = useState(false);
  const [inlineLoading, setInlineLoading] = useState(false);

  const flushBeforeRef = useRef(0);
  const hasMoreRef = useRef(true);
  const endCursorRef = useRef<string | undefined>(undefined);
  const loadingTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  // Stabilize the caller-provided loadOptions so loadMoreOptions / initialLoad
  // / onOpenChange / onScroll don't recreate when a parent passes loadOptions
  // inline. Same identity-stabilization pattern as in useDebouncedSearch.
  const loadOptionsRef = useRef(loadOptions);
  loadOptionsRef.current = loadOptions;

  const makeRequest = useCallback(
    async ({ mode, value, loadOptions }: MakeRequestArgs<T>) => {
      const currentTime = Date.now();
      const hasMore = hasMoreRef.current;

      if (mode === 'paginate' && !hasMore) {
        return;
      }

      if (mode === 'paginate') {
        setInlineLoading(true);
      } else {
        setInlineLoading(false);
        setLoading(true);
        // Only discard results for initial/search, not paginate
        flushBeforeRef.current = currentTime;
      }

      const endCursor = mode === 'paginate' ? endCursorRef.current : undefined;
      let result;
      try {
        result = await loadOptions({ mode, value, endCursor });
      } catch (error) {
        setLoading(false);
        setInlineLoading(false);
        throw error;
      }
      const flushBefore = flushBeforeRef.current;

      // Discard stale results — a newer request has superseded this one
      if (flushBefore && flushBefore > currentTime) {
        return;
      }

      hasMoreRef.current = result.hasMore;
      endCursorRef.current = result.endCursor;

      if (mode === 'paginate') {
        loadingTimerRef.current = setTimeout(() => {
          setInlineLoading(false);
        }, 30);
        setOptions((prev) => [...prev, ...result.newOptions]);
      } else {
        // Delay clearing loading to prevent a flash of "no results"
        loadingTimerRef.current = setTimeout(() => {
          setLoading(false);
        }, 30);
        setOptions(result.newOptions);
      }
    },
    [],
  );

  const loadMoreOptions = useCallback(
    (searchTerm?: string) => {
      // Fire-and-forget — makeRequest already cleans up loading state on error.
      // Catch to prevent unhandled rejection; pagination silently stops on failure.
      makeRequest({
        loadOptions: loadOptionsRef.current,
        mode: 'paginate',
        value: searchTerm,
        // eslint-disable-next-line no-console
      }).catch(console.error);
    },
    [makeRequest],
  );

  const initialLoad = useCallback(
    async (currentValue?: string | null) => {
      try {
        await makeRequest({
          loadOptions: loadOptionsRef.current,
          mode: 'initial',
          value: currentValue ?? undefined,
        });
        loadMoreOptions();
      } finally {
        setIsFirstLoad(false);
      }
    },
    [makeRequest, loadMoreOptions],
  );

  const resetState = useCallback(() => {
    clearTimeout(loadingTimerRef.current);
    setInlineLoading(false);
    setLoading(false);
    setIsFirstLoad(true);
    setOptions([]);
    // Flush everything before now — any in-flight request that resolves
    // after close should be discarded (its currentTime < Date.now()).
    flushBeforeRef.current = Date.now();
    hasMoreRef.current = true;
    endCursorRef.current = undefined;
  }, []);

  const onOpenChange = useCallback(
    (isOpen: boolean, value: string | null | undefined) => {
      passedOnOpenChange?.(isOpen);

      if (!isOpen) {
        resetState();
        return;
      }
      initialLoad(value);
    },
    [initialLoad, passedOnOpenChange, resetState],
  );

  // Scroll-based pagination — triggers loadMoreOptions when scrolled near bottom
  const onScroll = useCallback(
    (event: UIEvent<HTMLDivElement>, searchTerm?: string) => {
      if (loading || inlineLoading) {
        return;
      }
      const { scrollHeight, clientHeight, scrollTop } = event.currentTarget;
      if (shouldLoadMore(scrollHeight, clientHeight, scrollTop)) {
        loadMoreOptions(searchTerm);
      }
    },
    [loading, inlineLoading, loadMoreOptions],
  );

  return {
    options,
    setOptions,
    loading,
    inlineLoading,
    isFirstLoad,
    makeRequest,
    onOpenChange,
    onScroll,
  };
};
